import { VideoCamera } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import * as React from 'react';
import scrollIntoView from 'scroll-into-view';
import styled from 'styled-components';

import { useAppSelector } from '@/redux/hooks';
import {
    getCatalogHasVideo,
    getCatalogIsTimedPlus,
    getCatalogLongAddress,
    getCatalogTitle,
    getShouldShowLive,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getLiveCatalogStatus } from '@/redux/modules/console';
import { getLocale } from '@/redux/modules/intl';
import { popupCenter } from '@/utils/popupCenter';
import { getAuctionVideoUrl } from '@/utils/urls';

import CatalogDate from './components/CatalogDate/CatalogDate';

export type CatalogDetailsProps = {
    catalogId: number;
    forceIsLive: boolean;
};

const WIDTH = 1024;
const HEIGHT = 567;

const scrollToFooter = () => {
    const catalogFooter = document.getElementById('catalog-footer-header');
    scrollIntoView(catalogFooter);
};

const CatalogDetails = ({ catalogId, forceIsLive }: CatalogDetailsProps) => {
    const location = useAppSelector((state) => getCatalogLongAddress(state, catalogId));
    const hasVideo = useAppSelector((state) => getCatalogHasVideo(state, catalogId));
    const isTimedPlus = useAppSelector((state) => getCatalogIsTimedPlus(state, catalogId));
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const locale = useAppSelector(getLocale);

    // Live catalog data selectors
    const showLive = useAppSelector((state) => getShouldShowLive(state, catalogId));
    const liveStatus = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const isLive = showLive || liveStatus === 'live';
    const localePath = locale === 'en-gb' ? `/${locale}` : '';

    const openVideoConsole = React.useCallback(() => {
        if (!isLive) {
            return;
        }
        popupCenter({
            height: HEIGHT,
            title: `${title} Video`,
            url: localePath + getAuctionVideoUrl(catalogId),
            width: WIDTH,
        });
    }, [catalogId, isLive, localePath, title]);

    return (
        <Details data-testid="catalog-component-date">
            {hasVideo && !isTimedPlus && (
                <VideoCamera
                    className="mr-xs cursor-pointer xsMax:mb-xs xsMax:mr-0"
                    color="primary"
                    data-testid="video-indicator"
                    onClick={openVideoConsole}
                    size="sm"
                    style="fill"
                />
            )}
            <CatalogDate
                catalogId={catalogId}
                forceIsLive={forceIsLive}
            />
            <StyledAddressInformation>
                <BodySecondary color="secondary">{location}</BodySecondary>
            </StyledAddressInformation>
            <StyledAuctionDetailsContainer onClick={scrollToFooter}>
                <MoreInfoLink
                    color="secondary"
                    data-testid="catalogAuctionDetailsLink"
                >
                    <FormattedMessage id="auction_details" />
                </MoreInfoLink>
            </StyledAuctionDetailsContainer>
        </Details>
    );
};

export default CatalogDetails;

const Details = styled.section`
    margin-top: 30px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;

    & > span {
        height: 20px;
        line-height: 20px;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-bottom: 30px;
        margin-top: 0;
        flex-direction: column;
        text-align: center;
    }
`;

const StyledAddressInformation = styled.div`
    margin: 0 0 0 16px;
    padding: 0 16px 0 0;
    border-right: 1px solid ${({ theme }) => theme.colors.grey400};
    height: 100%;
    display: flex;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        border-right: none;
        margin: 0 0 8px 0;
        padding: 0;
    }
`;

const StyledAuctionDetailsContainer = styled.div`
    margin: 0 0 0 16px;
    padding: 0 16px 0 0;
    height: 100%;
    display: flex;
`;

const MoreInfoLink = styled(BodySecondary)`
    cursor: pointer;
    text-decoration: underline;

    &:hover {
        color: ${({ theme }) => theme.colors.blue200};
    }
`;
